import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import useUserData from 'hooks/useUserData';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FeedFilter } from 'api/KnowledgeAPI';
import RenderFilterFieldByType from './RenderFilterFieldByType';
import { getDefaultFilterValueByType } from '../helpers';
import { ConfigEntryFieldType, ConfigEntryType } from 'config/config';
import _ from 'lodash';
import UserAPI from 'api/UserAPI';
import { FieldWrapper, updateUserDetailsCache } from '@buildwise/libs/people';
import {
  CONFIGS,
  getCustomFieldsDefaultValues,
  getCustomFieldsValidationSchema,
  useConfigQuery,
} from '@buildwise/libs/configurations';
import usePostsFiltersStore from 'store/usePostsFiltersStore';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import ArticleTypeFilterField from './ArticleTypeFilterField';
import HasAttachmentsFilterField from './HasAttachmentsFilterField';
import DateFilterField from './DateFilterField';

type MoreFiltersModalProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  filters: Record<string, FeedFilter[]>;
  setFilters: (
    companyId: string,
    filters: FeedFilter[],
    tab: string,
    userFiltersSynced?: boolean,
  ) => void;
};

function MoreFiltersModal({ open, setOpen, filters, setFilters }: MoreFiltersModalProps) {
  const { pathname } = useLocation();
  const isSearchPage = pathname.includes('search');
  const { companyId, user } = useUserData();
  const { activeUniqueEntries } = useConfigQuery({
    companyId,
    configName: CONFIGS.COMPANY_FILTERS.configName,
  });
  const { [companyId]: allFilters = [] } = filters;
  const setAllFilters = setFilters;
  const selectedFilters: FeedFilter[] = useMemo(
    () =>
      allFilters.filter((filter) =>
        filter.filterValues && Array.isArray(filter.filterValues)
          ? filter.filterValues.length > 0
          : filter.filterValues || filter.startValue || filter.endValue,
      ),
    [allFilters],
  );
  const filterValues = useMemo(() => {
    const filterValues: Record<string, any> = {};
    selectedFilters.forEach((filter) => {
      filterValues[filter.filterName] = filter.filterValues;
    });

    return filterValues;
  }, [selectedFilters]);

  const getDefaultValues = useCallback(
    (filterValues?: Record<string, any>, isReset?: boolean) => {
      const additionalDefaultValues: Record<string, any> = {};
      const defaultValues = getCustomFieldsDefaultValues({
        data: filterValues,
        fields: activeUniqueEntries,
        // Note: Hide this until BE supports proper way to save date field values in user filters
        getPredefinedFieldDefaultValue: ({ field }) => {
          if (
            field.referenceObject?.type === ConfigEntryFieldType.DATE &&
            field.entryCode === 'createdAt'
          ) {
            if (!isReset) {
              const dateFilterValues = selectedFilters?.find(
                (filter) => filter.filterName === field.entryCode,
              );
              const startValue = dateFilterValues?.[`MIN_${field.entryCode}`] || null;
              const endValue = dateFilterValues?.[`MAX_${field.entryCode}`] || null;
              const duration = dateFilterValues?.['duration'] || null;
              additionalDefaultValues[field.entryCode] = startValue;
              additionalDefaultValues[`MIN_${field.entryCode}`] = startValue;
              additionalDefaultValues[`MAX_${field.entryCode}`] = endValue;
              additionalDefaultValues['duration'] = duration;
            } else {
              additionalDefaultValues[`MIN_${field.entryCode}`] = null;
              additionalDefaultValues[`MAX_${field.entryCode}`] = null;
              additionalDefaultValues['duration'] = null;
            }
          }
        },
      });
      return {
        ...defaultValues,
        ...additionalDefaultValues,
      };
    },
    [activeUniqueEntries, selectedFilters],
  );
  const filtersForm = useForm<Record<string, any>>({
    resolver: yupResolver(
      Yup.object().shape(
        getCustomFieldsValidationSchema({
          fields: activeUniqueEntries,
          doNotAddRequiredValidation: true,
        }),
      ),
    ),
    defaultValues: getDefaultValues(filterValues),
  });
  const {
    formState: { isSubmitting, errors },
    handleSubmit,
    reset,
    getValues,
  } = filtersForm;

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  const handleApply = useCallback(
    (values: Record<string, any>) => {
      setAllFilters(
        companyId,
        activeUniqueEntries.map((filter) => ({
          filterName: filter.entryCode,
          filterValues:
            filter.referenceObject?.type === ConfigEntryFieldType.DATE
              ? values[`MIN_${filter.entryCode}`]
                ? [
                    values[`MIN_${filter.entryCode}`],
                    values[`MAX_${filter.entryCode}`],
                    values['duration'],
                  ]
                : []
              : values[filter.entryCode] ||
                getDefaultFilterValueByType(filter.referenceObject?.type),
          ...(filter.referenceObject?.type === ConfigEntryFieldType.DATE &&
            filter.entryCode === 'createdAt' && {
              [`MIN_${filter.entryCode}`]: values?.[`MIN_${filter.entryCode}`] || null,
              [`MAX_${filter.entryCode}`]: values?.[`MAX_${filter.entryCode}`] || null,
              duration: values?.['duration'] || null,
            }),
        })),
        'articles',
      );

      closeModal();
    },
    [activeUniqueEntries],
  );

  const handleSaveFilters = useCallback(
    async (values: Record<string, any>) => {
      try {
        const newUserFilters = {};
        Object.keys(values).map((key) => {
          if (values[key] !== undefined || values[key] !== null) {
            newUserFilters[key] = Array.isArray(values[key])
              ? values[key]
              : values[key]
                ? [values[key]]
                : [];
          }
          if (key === 'createdAt' && values[`MIN_${key}`]) {
            newUserFilters['createdAt'] = [values[`MIN_${key}`], values[`MAX_${key}`]];
          }
        });
        const { data } = await UserAPI.editUser({
          ...user,
          userFilters: {
            ...user?.userFilters,
            ...newUserFilters,
          },
        });
        updateUserDetailsCache({
          ...user,
          ...data,
        });
        handleApply(values);
        toast.success('Filters are saved successfully!');
        closeModal();
      } catch (error) {
        console.log('SAVE_FILTERS_ERROR', error);
      }
    },
    [user],
  );

  const onApply = handleSubmit(handleApply);
  const onSave = handleSubmit(handleSaveFilters);

  const renderField = (filter: ConfigEntryType) => {
    switch (filter.entryCode) {
      case 'articleType': {
        return <ArticleTypeFilterField filter={filter} />;
      }
      case 'createdAt': {
        return <DateFilterField field={filter} />;
      }
      default: {
        return <RenderFilterFieldByType field={filter} />;
      }
    }
  };

  const resetFilters = () => {
    reset(getDefaultValues(undefined, true));
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      sx={{ '& .MuiPaper-root': { minHeight: '50%' } }}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      onClose={closeModal}
    >
      <CustomDialogTitle onClose={closeModal} id="dialog-title">
        <Title>Filters</Title>
      </CustomDialogTitle>
      <DialogContent dividers id="dialog-description" sx={{ py: 4 }}>
        <FormProvider {...filtersForm}>
          {activeUniqueEntries.map((filter) => {
            if (filter.entryCode === 'hasAttachments') {
              return <HasAttachmentsFilterField key={filter.id} filter={filter} />;
            }

            return (
              <FieldWrapper
                key={filter.id}
                id={filter.entryCode}
                label={filter.referenceObject?.label || filter.entryDescription}
                disabled={isSubmitting}
              >
                {renderField(filter)}
              </FieldWrapper>
            );
          })}
        </FormProvider>
      </DialogContent>
      <DialogActions sx={{ justifyContent: !isSearchPage ? 'space-between' : 'flex-end' }}>
        {!isSearchPage && (
          <Box ml={-1}>
            <Button variant="text" disabled={isSubmitting} onClick={onSave}>
              {isSubmitting ? (
                <CircularProgress
                  sx={(theme) => ({ color: theme.palette.custom.black })}
                  size={20}
                />
              ) : (
                'Set as Default'
              )}
            </Button>
          </Box>
        )}
        <Box>
          <Button variant="text" disabled={isSubmitting} onClick={resetFilters} sx={{ mr: 0.5 }}>
            Reset
          </Button>
          <Button variant="contained" disabled={isSubmitting} onClick={onApply}>
            Apply
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: theme.palette.custom.black,
}));

export default MoreFiltersModal;
